import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { Box, IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { Button, COLORS, Icon, ICONS, Modal } from "abs-ui-components";

import { callApi } from "../../../../../services/MonitoringPlanService";
import { emitToasterMsg } from "../../../../../services/toasterUtility";
import { API_ROUTES } from "../../FeuApiRoutes/FeuApiRoutes";

const FeuF1DefinitionsList = forwardRef(({ f1Abbreviations, refetchMonitoringData,vesselMstId }, ref) => {
    const RowActions = {
        Edit: 0,
        Add: 1,
    };

    const [activeAction, setactiveAction] = useState(-1);
    const [fuelData, setFuelData] = useState([]);
    const [validationErrors, setValidationErrors] = useState({});
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deletingRowIndex, setDeletingRowIndex] = useState();

    useEffect(() => {
        setFuelData(f1Abbreviations || [])
    }, [f1Abbreviations]);

    const handleFuelActions = async (table, row, action) => {
        if (activeAction === -1) {
            if (action === RowActions.Edit) {
                table.setEditingRow(row);
                setactiveAction(action);
            } else if (action === RowActions.Add) {
                table.setCreatingRow(true);
                setactiveAction(action);
            }
            return;
        } else return;
    };

    const resetActiveRow = () => {
        setactiveAction(-1);
        setValidationErrors({});
    };

    const validateRequired = (value) =>
        typeof value === "string" ? value.length > 0 :
            typeof value === "number" ? value >= 0 :
                false;
    const validateFuel = (row, type, rowObject) => {
        return {
            abbreviation: !validateRequired(row.abbreviation)
                ? "Abbreviation is Required"
                : "",

            explanation: !validateRequired(row.explanation)
                ? "Explanation is Required"
                : "",
        };
    }

    useImperativeHandle(ref, () => ({
        async submitAndCheck() {
            const { creatingRow, editingRow } = table.getState();
            if (!creatingRow && !editingRow) return true;
            const mode = creatingRow ? "add" : "edit";
            const editedValues = creatingRow ? creatingRow?._valuesCache : editingRow?._valuesCache;
            let isDirty = false;
            if (editingRow && editedValues) {
                isDirty = Object.keys(editedValues).some(
                    (key) => editedValues[key] !== editingRow.original[key]
                );
            }
            const validationRes = validateFuel(editedValues, mode, {});
            const hasErrors = Object.values(validationRes).some((err) => err);
            if (hasErrors) {
                setValidationErrors(validationRes);
                emitToasterMsg(
                    "warning",
                    "Validation Error",
                    "Please correct the highlighted fields before proceeding."
                );
                return false;
            }
            if (creatingRow) {
                await handleCreateAbbreviation({ values: editedValues, table }, true);
                return true;
            } else if (editingRow && isDirty) {
                await handleSaveAbbreviation({ values: editedValues, table, row: editingRow }, true);
                return true;
            }
            if (!isDirty) {
                setactiveAction(-1)
                table.setEditingRow(null);
                return true;
            }
        }
    }));

    const handleCreateAbbreviation = async ({ values, table }) => {
        const newValidationErrors = validateFuel(values, "add", {});
        if (Object.values(newValidationErrors).some((error) => error)) {
            setValidationErrors(newValidationErrors);
            return;
        }
        setValidationErrors({});
        const newRow = {
            abbreviation: values.abbreviation || "",
            explanation: values.explanation || "",
        };
        const updatedData = [...(fuelData || []), newRow];
        const finalPayload = updatedData?.map((item) => ({
            ...item,
            f1AbbreviationsId: 0
        }));
        try {
            const f1Abbr = await callApi({
                method: "POST",
                url: API_ROUTES.F1(vesselMstId),
                data: finalPayload,
            });
            if (f1Abbr?.isSuccess) {
                emitToasterMsg("success", "Table F.1", "Abbreviation added successfully");
                table.setCreatingRow(null);
                setFuelData(finalPayload);
                setactiveAction(-1);
                if (typeof refetchMonitoringData === "function") {
                    await refetchMonitoringData();
                }
            }
            else {
                emitToasterMsg("error", "Table F.1", f1Abbr?.errorMessage);
            }
        } catch (error) {
            emitToasterMsg("error", "Table F.1", error.errorMessage);
        }
    };

    const handleSaveAbbreviation = async ({ values, table, row }) => {
        const newValidationErrors = validateFuel(values, "edit", {});
        if (Object.values(newValidationErrors).some((error) => error)) {
            setValidationErrors(newValidationErrors);
            return;
        }
        setValidationErrors({});
        const updatedData = fuelData.map((item, index) => {
            if (index === row.index) {
                return {
                    ...item,
                    abbreviation: values.abbreviation || "",
                    explanation: values.explanation || "",
                };
            }
            return item;
        });
        const finalPayload = updatedData?.map((item) => ({
            ...item,
            f1AbbreviationsId: 0
        }));
        try {
            const f1Abbr = await callApi({
                method: "POST",
                url: API_ROUTES.F1(vesselMstId),
                data: finalPayload,
            });
            if (f1Abbr?.isSuccess) {
                emitToasterMsg("success", "Table F.1", "Abbreviation updated successfully");
                table.setEditingRow(null);
                setFuelData(finalPayload);
                setactiveAction(-1);
                if (typeof refetchMonitoringData === "function") {
                    await refetchMonitoringData();
                }
            }
            else {
                emitToasterMsg("error", "Table F.1", f1Abbr?.errorMessage);
            }
        } catch (error) {
            emitToasterMsg("error", "Table F.1", error.errorMessage);
        }
    };

    const handleDeleteAbbreviation = async (rowIndex) => {
        const updatedData = fuelData.filter((_, index) => index !== rowIndex);
        const finalPayload = updatedData?.map((item) => ({
            ...item,
            f1AbbreviationsId: 0
        }));
        try {
            const f1Abbr = await callApi({
                method: "POST",
                url: API_ROUTES.F1(vesselMstId),
                data: finalPayload,
            });
            if (f1Abbr?.isSuccess) {
                emitToasterMsg("success", "Table F.1", "Abbreviation deleted successfully");
                setactiveAction(-1);
                setFuelData(finalPayload);
                if (typeof refetchMonitoringData === "function") {
                    await refetchMonitoringData();
                }
            }
            else {
                emitToasterMsg("error", "Table F.1", f1Abbr?.errorMessage);
            }
        } catch (error) {
            emitToasterMsg("error", "Table F.1", error.errorMessage);
        }
        setConfirmDelete(false);
    };

    const columns = useMemo(
        () => [
            {
                accessorKey: "abbreviation",
                header: "Abbreviation, acronym, definition",
                muiEditTextFieldProps: ({ row }) => ({
                    required: true,
                    error: !!validationErrors?.abbreviation,
                    helperText: validationErrors?.abbreviation,
                    inputProps: {
                        maxLength: 1000,
                    },
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            abbreviation: undefined,
                        }),
                    onChange: (e) => {
                        const inputValue = e.target.value;
                        if (inputValue.length <= 1000) {
                            table.options.meta?.updateData(row.index, "abbreviation", inputValue);
                        }
                    },
                }),
            },
            {
                accessorKey: "explanation",
                header: "Explanation",
                muiEditTextFieldProps: ({ row }) => ({
                    required: true,
                    error: !!validationErrors?.explanation,
                    helperText: validationErrors?.explanation,
                    inputProps: {
                        maxLength: 1000,
                    },
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            explanation: undefined,
                        }),
                    onChange: (e) => {
                        const inputValue = e.target.value;
                        if (inputValue.length <= 1000) {
                            table.options.meta?.updateData(row.index, "explanation", inputValue);
                        }
                    },
                }),
            },
        ],
        [validationErrors]
    );

    const handleDeleteBtnClick = (rowIndex) => {
        setConfirmDelete(true);
        setDeletingRowIndex(rowIndex);
    };

    const table = useMaterialReactTable({
        columns,
        data: fuelData || [],
        enableStickyHeader: true,
        enableSorting: false,
        enableRowSelection: false,
        enableColumnPinning: false,
        enableTopToolbar: true,
        enableBottomToolbar: false,
        enableColumnActions: false,
        createDisplayMode: "row",
        editDisplayMode: "row",
        enableEditing: true,
        enablePagination: false,
        muiTableHeadCellProps: {
            sx: {
                color: "rgb(255 255 255)",
                backgroundColor: "#042a4c",
                position: "sticky",
                borderRight: "1px solid lightgray",
                "&:last-child": {
                    borderRight: "none",
                },
            },
        },
        muiTablePaperProps: {
            sx: {
                boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)",
            },
        },
        muiTableContainerProps: {
            sx: {
                maxHeight: "520px",
            },
        },
        displayColumnDefOptions: {
            'mrt-row-actions': {
                muiTableHeadCellProps: {
                    sx: {
                        borderBottom: 'none',
                        borderRight: '1px solid lightgray',
                    },
                },
            },
        },
        muiTableBodyCellProps: ({ row, cell }) => {
            return {
                sx: {
                    borderRight: "1px solid lightgray",
                    "&:last-child": {
                        borderRight: "none",
                    },
                    color: "",
                },
            };
        },
        muiTableBodyRowProps: ({ row }) => ({
            sx: {
                "&:not(:last-of-type)": {
                    borderBottom: "1px solid lightgray",
                },
                verticalAlign: "top",
            },
        }),
        muiTableHeadRowProps: {
            sx: {
                borderBottom: "2px solid lightgray",
            },
        },
        getRowId: (row) => { return row?.original?.b6CarbonCaptureTechnologyId || row?.b6CarbonCaptureTechnologyId },

        onCreatingRowSave: handleCreateAbbreviation,
        onEditingRowCancel: resetActiveRow,
        onCreatingRowCancel: resetActiveRow,
        onEditingRowSave: handleSaveAbbreviation,
        renderToolbarInternalActions: ({ table }) => <></>,
        renderRowActions: ({ row, table }) => {
            return (
                <Box sx={{ display: "flex", gap: "1rem" }}>
                    <Tooltip title="Edit">
                        <IconButton
                            style={{ padding: "0px" }}
                            onClick={() =>
                                handleFuelActions(
                                    table,
                                    row,
                                    RowActions.Edit
                                )
                            }
                        >
                            <EditIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton
                            style={{ padding: "0px" }}
                            color="error"
                            onClick={() => handleDeleteBtnClick(row?.index)}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            );
        },
        renderTopToolbarCustomActions: ({ table }) => (
            <div className="mt-1">
                <Button
                    startIcon={<Icon icon={ICONS.ADD} />}
                    onClick={() => {
                        handleFuelActions(table, null, RowActions.Add);
                    }}
                    disabled={!!table.getState().creatingRow || !!table.getState().editingRow}
                >
                    Add entry
                </Button>
            </div>
        ),
    });

    return (
        <>
            <div className="f1-definition-table mt-1">
                <MaterialReactTable table={table} />
            </div>
            <div className="confirmation-modal-container">
                <Modal
                    isOpen={confirmDelete}
                    modalHeader
                    padding={"70px"}
                    headerHeight={"50px"}
                    showFooter
                    onCloseClickListener={() => {
                        setConfirmDelete(false);
                    }}
                    footer={
                        <div className="d-flex justify-content-end fixed-action-btns p-4">
                            <Button
                                className="modal-delete-btn"
                                onClick={() => { handleDeleteAbbreviation(deletingRowIndex) }}
                                primaryColor={COLORS.RED.DARK}
                                primaryBackgroundColor={COLORS.RED.LIGHT}
                            >
                                Delete
                            </Button>
                            <Button
                                className="modal-cancel-btn"
                                onClick={() => {
                                    setConfirmDelete(false);
                                }}
                            >
                                Cancel
                            </Button>
                        </div>
                    }
                >
                    <div>Are you sure you want to delete this entry?</div>
                </Modal>
            </div>
        </>

    );
})
export default FeuF1DefinitionsList;